import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getUserFeeds, GetUserFeedsInput, GetUserFeedsOutput } from "../api";
import ApiAdapterError from "../../../utils/ApiAdapterError";
import { useFeedScope } from "../contexts/FeedScopeContext";

export const useUserFeedsInfinite = (
  input: Omit<GetUserFeedsInput, "search">,
  opts?: {
    disabled?: boolean;
    // Forces the personal feed list regardless of the page's ambient feed
    // scope. Without this, input.workspaceId === undefined falls through to the
    // current scope, which is wrong for callers (e.g. the personal→workspace
    // conversion dialog) that always need the personal list.
    forcePersonal?: boolean;
  },
) => {
  const [search, setSearch] = useState("");
  const useLimit = input.limit || 10;
  const { workspaceId } = useFeedScope();
  const scopedWorkspaceId = opts?.forcePersonal ? undefined : (input.workspaceId ?? workspaceId);

  const queryKey = [
    "user-feeds",
    {
      input: {
        ...input,
        workspaceId: scopedWorkspaceId,
        infinite: true,
        limit: useLimit,
        search,
      },
    },
  ];

  const {
    data,
    status,
    error,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isFetchedAfterMount,
    fetchStatus,
  } = useInfiniteQuery<GetUserFeedsOutput, ApiAdapterError>(
    queryKey,
    async ({ pageParam: newOffset }) => {
      const result = await getUserFeeds({
        ...input,
        workspaceId: scopedWorkspaceId,
        offset: newOffset,
        search,
      });

      return result;
    },
    {
      enabled: !opts?.disabled,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      // Returns the next offset
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.results.length < useLimit) {
          return undefined;
        }

        return allPages.length * useLimit;
      },
    },
  );

  return {
    data,
    status,
    error,
    fetchNextPage,
    isFetching,
    setSearch,
    hasNextPage,
    isFetchingNextPage,
    search: search || "",
    isFetchedAfterMount,
    fetchStatus,
  };
};
